'use client';

import Image from 'next/image';
import Link from 'next/link';
import { motion } from 'framer-motion';
import {
  Truck,
  MapPin,
  Fuel,
  Wrench,
  Shield,
  FileCheck,
  Route,
  BarChart3,
  Smartphone,
  Users,
  Zap,
  AlertTriangle,
  TrendingUp,
  CheckCircle2,
  Navigation,
  PlayCircle,
  UserCheck,
  Droplet,
  Calendar,
  Map,
  Video,
  FileText,
  Building2,
  ArrowRight,
  Sparkles,
  Activity,
  Eye,
  UserX,
  Car,
  Package,
} from 'lucide-react';
import { PAGE_HERO_IMAGES } from '@/lib/marketing-images';

export default function FleetManagementPage() {
  return (
    <div className="min-h-screen bg-background">
      {/* ══════════════════════════════════════════════════════
          HERO SECTION
          ══════════════════════════════════════════════════════ */}
      <section className="relative overflow-hidden gradient-hero pt-32 pb-20 lg:pt-40 lg:pb-32">
        <div className="absolute inset-0 grid-bg" />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-[#0f0e2e]" />

        <div className="relative mx-auto max-w-7xl px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="mx-auto max-w-4xl text-center"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.2, duration: 0.5 }}
              className="mb-6 inline-flex items-center gap-2 rounded-full glass-card px-4 py-1.5 text-xs font-medium text-neon-cyan"
            >
              <Truck className="h-3.5 w-3.5" />
              Fleet Management Solutions
            </motion.div>

            <h1 className="text-4xl font-bold tracking-tight text-white sm:text-5xl lg:text-6xl">
              Total Fleet <span className="text-gradient">Command</span>
            </h1>
            <p className="mt-6 text-lg leading-8 text-slate-300 sm:text-xl">
              Complete visibility and control over every vehicle, driver, and trip. Transform your fleet operations with real-time tracking, AI-powered insights, and automated compliance.
            </p>

            <div className="mx-auto mt-8 mb-2 max-w-4xl">
              <div className="glass-card-heavy rounded-3xl p-2 border border-white/10">
                <Image
                  src={PAGE_HERO_IMAGES.fleetManagementIndustry.src}
                  alt={PAGE_HERO_IMAGES.fleetManagementIndustry.alt}
                  width={1600}
                  height={900}
                  className="h-auto w-full rounded-2xl object-cover"
                  priority
                />
              </div>
            </div>

            <div className="mt-10 flex flex-col items-center justify-center gap-4 sm:flex-row">
              <Link
                href="/contact"
                className="btn-neon rounded-xl px-8 py-3.5 text-sm font-semibold"
              >
                Get Started
                <ArrowRight className="ml-2 inline h-4 w-4" />
              </Link>
              <Link
                href="/features"
                className="btn-neon-outline rounded-xl px-8 py-3.5 text-sm font-semibold"
              >
                Explore Features
              </Link>
            </div>
          </motion.div>

          {/* Floating icons */}
          <div className="relative mt-20 h-64">
            {[
              { Icon: Truck, x: '10%', y: '20%', delay: 0 },
              { Icon: MapPin, x: '80%', y: '15%', delay: 0.5 },
              { Icon: Fuel, x: '85%', y: '70%', delay: 1 },
              { Icon: Shield, x: '15%', y: '75%', delay: 1.5 },
              { Icon: Route, x: '50%', y: '5%', delay: 0.8 },
            ].map(({ Icon, x, y, delay }) => (
              <motion.div
                key={`${x}-${y}`}
                className="absolute glass-card rounded-xl p-3"
                style={{ left: x, top: y }}
                animate={{ y: [0, -10, 0] }}
                transition={{ duration: 4, repeat: Infinity, delay }}
              >
                <Icon className="h-5 w-5 text-neon-cyan/70" />
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════
          CHALLENGES SECTION
          ══════════════════════════════════════════════════════ */}
      <section className="relative py-24 lg:py-32 gradient-section">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="mx-auto max-w-2xl text-center mb-16"
          >
            <span className="inline-flex items-center gap-2 rounded-full glass-card px-4 py-1.5 text-xs font-medium text-neon-magenta mb-4">
              <AlertTriangle className="h-3.5 w-3.5" />
              Common Challenges
            </span>
            <h2 className="text-3xl font-bold text-white sm:text-4xl lg:text-5xl">
              The Fleet Management <span className="text-gradient-warm">Pain Points</span>
            </h2>
            <p className="mt-4 text-base text-slate-300">
              Every fleet manager faces these critical challenges. We solve them all.
            </p>
          </motion.div>

          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {[
              {
                icon: Fuel,
                title: 'Fuel Theft & Waste',
                desc: 'Unauthorized fuel purchases and inefficient driving patterns drain your budget.',
                color: 'text-neon-magenta',
              },
              {
                icon: Eye,
                title: 'No Real-Time Visibility',
                desc: 'Blind spots in your operations lead to delayed responses and missed opportunities.',
                color: 'text-neon-purple',
              },
              {
                icon: Wrench,
                title: 'Maintenance Backlogs',
                desc: 'Reactive maintenance causes unexpected breakdowns and costly repairs.',
                color: 'text-neon-blue',
              },
              {
                icon: UserX,
                title: 'Driver Behavior Issues',
                desc: 'Harsh braking, speeding, and unauthorized usage increase risk and costs.',
                color: 'text-neon-cyan',
              },
              {
                icon: FileCheck,
                title: 'Regulatory Compliance',
                desc: 'Complex DOT, ELD, and safety regulations require constant monitoring and reporting.',
                color: 'text-neon-teal',
              },
              {
                icon: AlertTriangle,
                title: 'Unauthorized Usage',
                desc: 'Personal use, route deviations, and after-hours usage go undetected.',
                color: 'text-neon-amber',
              },
            ].map((challenge, i) => (
              <motion.div
                key={challenge.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1, duration: 0.5 }}
                className="glass-card rounded-2xl p-6 group"
              >
                <div className={`mb-4 inline-flex rounded-xl bg-white/8 p-3 ${challenge.color} transition-colors group-hover:bg-white/10`}>
                  <challenge.icon className="h-5 w-5" />
                </div>
                <h3 className="mb-2 text-lg font-semibold text-white">{challenge.title}</h3>
                <p className="text-sm leading-relaxed text-slate-300">{challenge.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════
          SOLUTION SECTION
          ══════════════════════════════════════════════════════ */}
      <section className="relative py-24 lg:py-32 gradient-section-purple">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="mx-auto max-w-2xl text-center mb-16"
          >
            <span className="inline-flex items-center gap-2 rounded-full glass-card px-4 py-1.5 text-xs font-medium text-neon-purple mb-4">
              <Sparkles className="h-3.5 w-3.5" />
              Our Solution
            </span>
            <h2 className="text-3xl font-bold text-white sm:text-4xl lg:text-5xl">
              Complete Fleet <span className="text-gradient">Intelligence Platform</span>
            </h2>
            <p className="mt-4 text-base text-slate-300">
              Real-time tracking, AI-powered analytics, and automated workflows that transform how you manage your fleet.
            </p>
          </motion.div>

          <div className="grid gap-8 lg:grid-cols-2">
            {[
              {
                icon: Navigation,
                title: 'Real-Time GPS Tracking',
                desc: 'Sub-second location updates with live maps, geofencing, and route replay. Know exactly where every vehicle is, 24/7.',
                color: 'from-neon-cyan to-neon-blue',
              },
              {
                icon: UserCheck,
                title: 'Driver Behavior Monitoring',
                desc: 'Safety scorecards, harsh event detection, and driver coaching tools. Reduce accidents and insurance costs.',
                color: 'from-neon-purple to-neon-magenta',
              },
              {
                icon: Droplet,
                title: 'Fuel Analytics & Theft Prevention',
                desc: 'Track fuel consumption, detect anomalies, and prevent unauthorized fuel purchases with automated alerts.',
                color: 'from-neon-teal to-neon-cyan',
              },
              {
                icon: Calendar,
                title: 'Predictive Maintenance Scheduling',
                desc: 'AI-powered maintenance alerts based on mileage, engine hours, and usage patterns. Prevent breakdowns before they happen.',
                color: 'from-neon-blue to-neon-purple',
              },
              {
                icon: FileCheck,
                title: 'Compliance Automation',
                desc: 'Automated ELD logs, DOT reporting, and safety documentation. Stay compliant without the manual work.',
                color: 'from-neon-amber to-neon-magenta',
              },
              {
                icon: Route,
                title: 'AI Route Optimization',
                desc: 'Reduce fuel costs and delivery times with intelligent route planning and real-time traffic adjustments.',
                color: 'from-neon-cyan to-neon-teal',
              },
            ].map((solution, i) => (
              <motion.div
                key={solution.title}
                initial={{ opacity: 0, x: i % 2 === 0 ? -20 : 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1, duration: 0.5 }}
                className="glass-card-heavy rounded-2xl p-8 group"
              >
                <div className={`mb-5 inline-flex rounded-xl bg-gradient-to-br ${solution.color} p-4 text-white shadow-lg`}>
                  <solution.icon className="h-6 w-6" />
                </div>
                <h3 className="mb-3 text-xl font-semibold text-white">{solution.title}</h3>
                <p className="text-sm leading-relaxed text-slate-300">{solution.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════
          FEATURES GRID
          ══════════════════════════════════════════════════════ */}
      <section className="relative py-24 lg:py-32 gradient-section">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="mx-auto max-w-2xl text-center mb-16"
          >
            <span className="inline-flex items-center gap-2 rounded-full glass-card px-4 py-1.5 text-xs font-medium text-neon-cyan mb-4">
              <Zap className="h-3.5 w-3.5" />
              Platform Features
            </span>
            <h2 className="text-3xl font-bold text-white sm:text-4xl lg:text-5xl">
              Everything You Need in <span className="text-gradient">One Platform</span>
            </h2>
            <p className="mt-4 text-base text-slate-300">
              Comprehensive fleet management tools that work together seamlessly.
            </p>
          </motion.div>

          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {[
              { icon: MapPin, title: 'Live GPS Tracking', desc: 'Real-time location with sub-second updates' },
              { icon: PlayCircle, title: 'Trip Replay', desc: 'Review complete trip history with playback' },
              { icon: UserCheck, title: 'Driver Scorecards', desc: 'Safety and performance metrics per driver' },
              { icon: Droplet, title: 'Fuel Monitoring', desc: 'Consumption tracking and theft detection' },
              { icon: Wrench, title: 'Maintenance Alerts', desc: 'Predictive scheduling and reminders' },
              { icon: Map, title: 'Geofencing', desc: 'Virtual boundaries with entry/exit alerts' },
              { icon: Video, title: 'Camera AI', desc: 'ADAS/DMS with real-time safety alerts' },
              { icon: FileText, title: '100+ Reports', desc: 'Comprehensive analytics and insights' },
              { icon: Smartphone, title: 'Mobile App', desc: 'iOS & Android apps for drivers and managers' },
              { icon: Users, title: 'Multi-Tenant', desc: 'Manage multiple fleets from one dashboard' },
              { icon: Shield, title: 'Security & Compliance', desc: 'Enterprise-grade security and audit logs' },
              { icon: BarChart3, title: 'Advanced Analytics', desc: 'AI-powered insights and predictions' },
            ].map((feature, i) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.05, duration: 0.4 }}
                className="glass-card rounded-xl p-6 group hover:border-neon-cyan/20 transition-colors"
              >
                <div className="mb-4 inline-flex rounded-lg bg-white/8 p-2.5 text-neon-cyan transition-colors group-hover:bg-neon-cyan/10">
                  <feature.icon className="h-5 w-5" />
                </div>
                <h3 className="mb-1.5 text-base font-semibold text-white">{feature.title}</h3>
                <p className="text-sm text-slate-300">{feature.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════
          USE CASES
          ══════════════════════════════════════════════════════ */}
      <section className="relative py-24 lg:py-32 gradient-section-purple">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="mx-auto max-w-2xl text-center mb-16"
          >
            <span className="inline-flex items-center gap-2 rounded-full glass-card px-4 py-1.5 text-xs font-medium text-neon-purple mb-4">
              <Building2 className="h-3.5 w-3.5" />
              Use Cases
            </span>
            <h2 className="text-3xl font-bold text-white sm:text-4xl lg:text-5xl">
              Trusted by Fleet Operators <span className="text-gradient">Across Industries</span>
            </h2>
            <p className="mt-4 text-base text-slate-300">
              From logistics to construction, we power fleet operations of all sizes.
            </p>
          </motion.div>

          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {[
              {
                icon: Truck,
                title: 'Logistics Companies',
                desc: 'Optimize delivery routes, track shipments, and ensure on-time delivery with real-time visibility.',
                color: 'from-neon-cyan to-neon-blue',
              },
              {
                icon: Building2,
                title: 'Construction Fleets',
                desc: 'Monitor heavy equipment, track job site arrivals, and manage fuel consumption across multiple sites.',
                color: 'from-neon-purple to-neon-magenta',
              },
              {
                icon: Shield,
                title: 'Government Fleets',
                desc: 'Ensure compliance, track public vehicle usage, and optimize maintenance schedules for municipal fleets.',
                color: 'from-neon-teal to-neon-cyan',
              },
              {
                icon: Car,
                title: 'Rental Car Companies',
                desc: 'Track rental vehicles, prevent unauthorized usage, and optimize fleet utilization and maintenance.',
                color: 'from-neon-blue to-neon-purple',
              },
              {
                icon: Package,
                title: 'Courier Services',
                desc: 'Real-time package tracking, route optimization, and proof-of-delivery for last-mile logistics.',
                color: 'from-neon-amber to-neon-magenta',
              },
              {
                icon: Wrench,
                title: 'Mining Vehicles',
                desc: 'Monitor heavy-duty equipment in remote locations, track fuel consumption, and schedule maintenance.',
                color: 'from-neon-cyan to-neon-teal',
              },
            ].map((useCase, i) => (
              <motion.div
                key={useCase.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1, duration: 0.5 }}
                className="glass-card rounded-2xl p-8 group"
              >
                <div className={`mb-5 inline-flex rounded-xl bg-gradient-to-br ${useCase.color} p-3 text-white shadow-lg`}>
                  <useCase.icon className="h-5 w-5" />
                </div>
                <h3 className="mb-2 text-lg font-semibold text-white">{useCase.title}</h3>
                <p className="text-sm leading-relaxed text-slate-300">{useCase.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════
          STATS SECTION
          ══════════════════════════════════════════════════════ */}
      <section className="relative py-24 lg:py-32 gradient-section">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="mx-auto max-w-2xl text-center mb-16"
          >
            <span className="inline-flex items-center gap-2 rounded-full glass-card px-4 py-1.5 text-xs font-medium text-neon-teal mb-4">
              <TrendingUp className="h-3.5 w-3.5" />
              Proven Results
            </span>
            <h2 className="text-3xl font-bold text-white sm:text-4xl lg:text-5xl">
              Real Impact on Your <span className="text-gradient-cool">Bottom Line</span>
            </h2>
            <p className="mt-4 text-base text-slate-300">
              Fleet operators see measurable improvements across all key metrics.
            </p>
          </motion.div>

          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {[
              {
                value: '20%',
                label: 'Fuel Savings',
                desc: 'Reduced fuel costs through route optimization and driver behavior monitoring',
                icon: Droplet,
                color: 'text-neon-cyan',
              },
              {
                value: '35%',
                label: 'Fewer Accidents',
                desc: 'Improved safety scores and reduced incidents with driver coaching',
                icon: Shield,
                color: 'text-neon-purple',
              },
              {
                value: '40%',
                label: 'Less Unauthorized Usage',
                desc: 'Dramatic reduction in personal use and unauthorized trips',
                icon: CheckCircle2,
                color: 'text-neon-blue',
              },
              {
                value: '99.9%',
                label: 'Uptime',
                desc: 'Enterprise-grade reliability with minimal downtime',
                icon: Activity,
                color: 'text-neon-teal',
              },
            ].map((stat, i) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1, duration: 0.5 }}
                className="glass-card-heavy rounded-2xl p-8 text-center group"
              >
                <div className={`mb-4 inline-flex rounded-xl bg-white/8 p-3 ${stat.color} transition-colors group-hover:bg-white/10`}>
                  <stat.icon className="h-6 w-6" />
                </div>
                <div className={`text-4xl font-bold ${stat.color} mb-2`}>{stat.value}</div>
                <div className="mb-2 text-lg font-semibold text-white">{stat.label}</div>
                <p className="text-sm text-slate-300">{stat.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════
          CTA SECTION
          ══════════════════════════════════════════════════════ */}
      <section className="relative py-24 lg:py-32 gradient-hero">
        <div className="absolute inset-0 grid-bg" />
        <div className="relative mx-auto max-w-7xl px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="mx-auto max-w-3xl text-center"
          >
            <div className="glass-card-heavy rounded-3xl p-12 lg:p-16">
              <motion.div
                initial={{ scale: 0.9 }}
                whileInView={{ scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: 0.2, duration: 0.5 }}
                className="mb-6 inline-flex rounded-full bg-gradient-to-r from-neon-cyan to-neon-purple p-1"
              >
                <div className="rounded-full bg-background px-4 py-1.5">
                  <span className="text-sm font-semibold text-gradient">Ready to Transform Your Fleet?</span>
                </div>
              </motion.div>

              <h2 className="text-3xl font-bold text-white sm:text-4xl lg:text-5xl mb-4">
                Start Your Fleet <span className="text-gradient">Transformation Today</span>
              </h2>
              <p className="text-lg text-slate-300 mb-8">
                Join thousands of fleet operators who trust GlonexTrack for complete visibility, control, and intelligence.
              </p>

              <div className="flex flex-col items-center justify-center gap-4 sm:flex-row">
                <Link
                  href="/contact"
                  className="btn-neon rounded-xl px-8 py-3.5 text-sm font-semibold"
                >
                  Schedule Demo
                  <ArrowRight className="ml-2 inline h-4 w-4" />
                </Link>
                <Link
                  href="/pricing"
                  className="btn-neon-outline rounded-xl px-8 py-3.5 text-sm font-semibold"
                >
                  View Pricing
                </Link>
              </div>

              <div className="mt-8 flex flex-wrap items-center justify-center gap-6 text-sm text-slate-300">
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4 text-neon-cyan" />
                  <span>No credit card required</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4 text-neon-cyan" />
                  <span>14-day free trial</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4 text-neon-cyan" />
                  <span>Enterprise support</span>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
